import { useState, useCallback } from 'react';
import {
  formatFileSize,
  decryptMessage,
  encodeBase64,
  sealDecryptFileBytes,
  epochKeyRing,
} from '@clinikchat/shared';
import { decryptFileBytesAsync } from '../../lib/crypto-worker';
import api from '../../lib/api';

interface AttachmentData {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  url?: string;
  thumbnailUrl?: string | null;
  /**
   * Channel key epoch this attachment was encrypted under. Null for
   * pre-epoch uploads, which decrypt against the legacy flat channelKey.
   */
  keyEpoch?: number | null;
  /** True when this attachment has dual-blob DM encryption (senderStorageKey + recipientStorageKey set). */
  isDmDualBlob?: boolean;
}

interface Props {
  attachments: AttachmentData[];
  onImageClick: (url: string, index: number) => void;
  /** True when the parent message was E2EE-encrypted. */
  encrypted?: boolean;
  /** Symmetric channel key (base64) used to decrypt file bytes and metadata. */
  channelKey?: string | null;
  /** Channel id — required to resolve per-epoch keys from the ring. */
  channelId?: string;
  /** Own secret key (base64) used to decrypt DM dual-blob attachments. */
  mySecretKey?: string | null;
  /**
   * Parsed JSON the server stores in a FILE message's `content`: encrypted
   * filename/MIME for channel files, or the display filename for DM files.
   * `attachment.filename` is always the opaque 'encrypted.bin' placeholder.
   */
  fileMeta?: FileMessageMeta | null;
}

export interface FileMessageMeta {
  encryptedFilename?: string;
  encryptedMimeType?: string;
  dmDualBlob?: boolean;
  filename?: string;
  originalSize?: number;
}

/**
 * A FILE message's `content` is metadata JSON, not an encrypted text body, so it
 * must never go through text decryption. Returns the message with an empty body
 * and the parsed metadata for AttachmentView to decrypt the filename/MIME.
 */
export function withFileMeta<T extends { content: string }>(
  message: T,
): T & { fileMeta: FileMessageMeta | null } {
  return { ...message, content: '', fileMeta: parseFileMessageMeta(message.content) };
}

/** Parse a FILE message's content JSON; null for anything that is not an object. */
export function parseFileMessageMeta(content: string): FileMessageMeta | null {
  try {
    const parsed: unknown = JSON.parse(content);
    return parsed && typeof parsed === 'object' ? (parsed as FileMessageMeta) : null;
  } catch {
    return null;
  }
}

/**
 * Resolve the right base64 key to decrypt this attachment with.
 * An epoch-tagged attachment uses its own epoch key from the ring; only attachments
 * with no epoch (pre-epoch uploads) use the legacy flat channelKey. Returns null when
 * the right key is not available (caller surfaces the decryption-failure UI state).
 */
function resolveDecryptionKey(
  attachment: AttachmentData,
  channelId: string | undefined,
  channelKey: string | null | undefined,
): string | null {
  if (
    channelId &&
    typeof attachment.keyEpoch === 'number' &&
    Number.isInteger(attachment.keyEpoch)
  ) {
    const entry = epochKeyRing.get(channelId, attachment.keyEpoch);
    if (entry) return encodeBase64(entry);
    // An epoch-tagged upload is never decryptable with the legacy flat key: using it
    // would just fail with "Decryption failed". Report the key as unavailable so the
    // caller shows that (and the epoch backfill can fill it in).
    return null;
  }
  return channelKey ?? null;
}

/** The ciphertext could not be downloaded (network, CORS, expired or refused URL). */
class AttachmentFetchError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AttachmentFetchError';
  }
}

/**
 * Download the ciphertext of an attachment THROUGH the API (authenticated, same
 * origin family as every other call). Fetching the bucket's signed URL straight from
 * the browser depends on the bucket's CORS rules and reachability and leaks a
 * bearer URL into the page; the API relays the same opaque ciphertext without either.
 * For DM dual blobs the API serves the copy sealed for the requesting user.
 */
async function downloadCiphertext(attachment: { id: string }): Promise<Uint8Array> {
  try {
    const res = await api.get<ArrayBuffer>(`/api/v1/attachments/${attachment.id}/content`, {
      responseType: 'arraybuffer',
    });
    return new Uint8Array(res.data);
  } catch (err) {
    throw new AttachmentFetchError(`Download failed: ${String(err)}`);
  }
}

/**
 * Download an encrypted attachment, decrypt it with the channel key, and
 * trigger a browser download of the plaintext bytes.
 */
async function decryptAndDownload(
  attachment: { id: string },
  channelKey: string,
  encryptedFilenameB64: string,
  encryptedMimeTypeB64: string,
): Promise<void> {
  const encryptedBytes = await downloadCiphertext(attachment);
  const encryptedB64 = encodeBase64(encryptedBytes);
  const plainBytes = await decryptFileBytesAsync(encryptedB64, channelKey);
  if (!plainBytes) throw new Error('Decryption failed — wrong key or corrupt data');

  const filename = decryptMessage(encryptedFilenameB64, channelKey) ?? 'file';
  const mimeType = decryptMessage(encryptedMimeTypeB64, channelKey) ?? 'application/octet-stream';

  const normalizedBytes = new Uint8Array(plainBytes);
  const blob = new Blob([normalizedBytes], { type: mimeType });
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
}

/**
 * Fetch the presigned URL for a DM dual-blob attachment (the API returns the
 * blob sealed for the requesting user), download it, decrypt with the caller's
 * secret key, and trigger a browser download.
 */
async function dmDecryptAndDownload(
  attachmentId: string,
  mySecretKey: string,
  displayFilename: string,
): Promise<void> {
  const encryptedBytes = await downloadCiphertext({ id: attachmentId });
  const encryptedB64 = encodeBase64(encryptedBytes);
  const plainBytes = sealDecryptFileBytes(encryptedB64, mySecretKey);
  if (!plainBytes) throw new Error('Decryption failed — wrong key or corrupt data');

  const normalizedBytes = new Uint8Array(plainBytes);
  const blob = new Blob([normalizedBytes], { type: 'application/octet-stream' });
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = displayFilename || 'file';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
}

interface DmEncryptedFileButtonProps {
  attachment: AttachmentData;
  mySecretKey: string;
  fileMeta?: FileMessageMeta | null;
}

function DmEncryptedFileButton({ attachment, mySecretKey, fileMeta }: DmEncryptedFileButtonProps) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'error' | 'fetch-error'>('idle');
  const displayName = fileMeta?.filename || null;

  const handleClick = useCallback(async () => {
    setStatus('loading');
    try {
      await dmDecryptAndDownload(
        attachment.id,
        mySecretKey,
        fileMeta?.filename || attachment.filename,
      );
      setStatus('idle');
    } catch (err) {
      console.error('DM attachment download failed', err);
      setStatus(err instanceof AttachmentFetchError ? 'fetch-error' : 'error');
    }
  }, [attachment, mySecretKey, fileMeta]);

  return (
    <button
      onClick={handleClick}
      disabled={status === 'loading'}
      className="flex items-center gap-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 hover:bg-gray-100 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10 disabled:opacity-50 w-full text-left"
    >
      <svg
        className="h-8 w-8 flex-shrink-0 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
        />
      </svg>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-primary">
          {status === 'loading'
            ? 'Decrypting…'
            : status === 'fetch-error'
              ? 'Download failed — click to retry'
              : status === 'error'
                ? 'Decryption failed'
                : (displayName ?? 'Encrypted file')}
        </p>
        <p className="text-xs text-gray-400 dark:text-slate-500">
          {formatFileSize(attachment.size)} · click to decrypt &amp; download
        </p>
      </div>
      <svg
        className="ml-auto h-4 w-4 flex-shrink-0 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
        />
      </svg>
    </button>
  );
}

interface EncryptedFileButtonProps {
  attachment: AttachmentData;
  channelKey: string;
  fileMeta?: FileMessageMeta | null;
}

function EncryptedFileButton({ attachment, channelKey, fileMeta }: EncryptedFileButtonProps) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'error' | 'fetch-error'>('idle');
  const displayName = fileMeta?.encryptedFilename
    ? decryptMessage(fileMeta.encryptedFilename, channelKey)
    : null;

  const handleClick = useCallback(async () => {
    setStatus('loading');
    try {
      // The server stores encrypted filename and mime type in the attachment's
      // filename field as JSON: { encryptedFilename, encryptedMimeType }. If the
      // field is the legacy 'encrypted.bin' fallback, use empty strings and let
      // decryptMessage return null gracefully.
      let encFilename = fileMeta?.encryptedFilename ?? '';
      let encMime = fileMeta?.encryptedMimeType ?? '';
      if (!encFilename) {
        // Legacy rows stored the encrypted names in attachment.filename.
        try {
          const meta = JSON.parse(attachment.filename) as {
            encryptedFilename?: string;
            encryptedMimeType?: string;
          };
          encFilename = meta.encryptedFilename ?? '';
          encMime = meta.encryptedMimeType ?? '';
        } catch {
          // filename is a raw ciphertext blob — treat as the filename ciphertext directly
          encFilename = attachment.filename;
        }
      }
      await decryptAndDownload(attachment, channelKey, encFilename, encMime);
      setStatus('idle');
    } catch (err) {
      // Keep the cause visible in the console: "Decryption failed" used to cover a
      // failed download as well, which made a storage problem look like a key problem.
      console.error('Attachment download failed', err);
      setStatus(err instanceof AttachmentFetchError ? 'fetch-error' : 'error');
    }
  }, [attachment, channelKey, fileMeta]);

  return (
    <button
      onClick={handleClick}
      disabled={status === 'loading'}
      className="flex items-center gap-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 hover:bg-gray-100 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10 disabled:opacity-50 w-full text-left"
    >
      <svg
        className="h-8 w-8 flex-shrink-0 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
        />
      </svg>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-primary">
          {status === 'loading'
            ? 'Decrypting…'
            : status === 'fetch-error'
              ? 'Download failed — click to retry'
              : status === 'error'
                ? 'Decryption failed'
                : (displayName ?? 'Encrypted file')}
        </p>
        <p className="text-xs text-gray-400 dark:text-slate-500">
          {formatFileSize(attachment.size)} · click to decrypt &amp; download
        </p>
      </div>
      <svg
        className="ml-auto h-4 w-4 flex-shrink-0 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
        />
      </svg>
    </button>
  );
}

export default function AttachmentView({
  attachments,
  onImageClick,
  encrypted,
  channelKey,
  channelId,
  mySecretKey,
  fileMeta,
}: Props) {
  if (attachments.length === 0) return null;

  // DM dual-blob attachments: any attachment in this list that has isDmDualBlob
  // was sealed with the viewer's public key. Decrypt using the viewer's secret key.
  const hasDmBlobs = attachments.some((a) => a.isDmDualBlob);
  if (hasDmBlobs && mySecretKey) {
    return (
      <div className="mt-1 space-y-1">
        {attachments.map((a) => (
          <DmEncryptedFileButton
            key={a.id}
            attachment={a}
            mySecretKey={mySecretKey}
            fileMeta={fileMeta}
          />
        ))}
      </div>
    );
  }

  // DM dual-blob but secret key not yet available — show placeholder.
  if (hasDmBlobs && !mySecretKey) {
    return (
      <div className="mt-1 space-y-1">
        {attachments.map((a) => (
          <div
            key={a.id}
            className="flex items-center gap-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 dark:border-white/10 dark:bg-white/5 text-sm text-gray-400"
          >
            <svg
              className="h-8 w-8 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <span>[Encrypted file — key unavailable]</span>
          </div>
        ))}
      </div>
    );
  }

  // For PRIVATE channel encrypted messages, all attachments are opaque ciphertext
  // blobs stored with mimeType 'application/octet-stream'. Render a decrypt-on-click
  // button keyed by the per-attachment epoch (falls back to flat channelKey).
  if (encrypted) {
    const decryptionKeys = attachments.map((a) => ({
      attachment: a,
      key: resolveDecryptionKey(a, channelId, channelKey),
    }));
    // Epoch-tagged files whose key is not held (yet) still render their own
    // "key for epoch N unavailable" line instead of falling through to the plain list.
    const anyDecryptable = decryptionKeys.some(
      (e) => e.key !== null || typeof e.attachment.keyEpoch === 'number',
    );
    if (anyDecryptable) {
      return (
        <div className="mt-1 space-y-1">
          {decryptionKeys.map(({ attachment, key }) =>
            key ? (
              <EncryptedFileButton
                key={attachment.id}
                attachment={attachment}
                channelKey={key}
                fileMeta={fileMeta}
              />
            ) : (
              <div
                key={attachment.id}
                className="flex items-center gap-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 dark:border-white/10 dark:bg-white/5 text-sm text-gray-400"
              >
                <span>[Encrypted file — key for epoch {attachment.keyEpoch} unavailable]</span>
              </div>
            ),
          )}
        </div>
      );
    }
  }

  // Encrypted message but no key (legacy or epoch) is available — placeholder.
  if (encrypted && !channelKey) {
    return (
      <div className="mt-1 space-y-1">
        {attachments.map((a) => (
          <div
            key={a.id}
            className="flex items-center gap-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 dark:border-white/10 dark:bg-white/5 text-sm text-gray-400"
          >
            <svg
              className="h-8 w-8 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <span>[Encrypted file — key unavailable]</span>
          </div>
        ))}
      </div>
    );
  }

  // Plaintext path — original rendering.
  const images = attachments.filter((a) => a.mimeType.startsWith('image/') && a.url);
  const files = attachments.filter((a) => !a.mimeType.startsWith('image/'));

  return (
    <div className="mt-1 space-y-1">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((img, idx) => (
            <button
              key={img.id}
              onClick={() => onImageClick(img.url!, idx)}
              className="overflow-hidden rounded border border-gray-200 hover:opacity-90 dark:border-white/10"
            >
              <img
                src={img.thumbnailUrl || img.url}
                alt={img.filename}
                className="max-h-48 max-w-xs object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}

      {files.map((f) => (
        <a
          key={f.id}
          href={f.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 hover:bg-gray-100 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10"
        >
          <svg
            className="h-8 w-8 flex-shrink-0 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-primary">{f.filename}</p>
            <p className="text-xs text-gray-400 dark:text-slate-500">{formatFileSize(f.size)}</p>
          </div>
          <svg
            className="ml-auto h-4 w-4 flex-shrink-0 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </a>
      ))}
    </div>
  );
}
